/*
  Variant auto-select.

  When a shopper picks an option whose combination with the other current
  selections does not exist (e.g. switching from a single flavor — only sold in
  10-packs — to a variety — only sold in 9-packs), this keeps the option they just
  changed and snaps the conflicting option(s) to a real, available variant,
  preferring to preserve the earlier options (e.g. nicotine strength).

  Works in both directions (change flavor -> fix pack, or change pack -> fix flavor).

  Delegated from document in the capture phase so it (a) runs before the theme's
  <variant-selects> change handler and (b) keeps working after the theme replaces
  the picker DOM when it re-renders product info on a variant change.
*/
(function () {
  function getVariants(root) {
    const script = root.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function getFieldsets(root) {
    return Array.from(root.querySelectorAll('fieldset'));
  }

  function checkedValue(fieldset) {
    const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => input.checked);
    return radio ? radio.value : undefined;
  }

  function onChange(root, event) {
    const target = event.target;
    if (!target || target.type !== 'radio') return;

    const variants = getVariants(root);
    if (!variants || !variants.length) return;

    const fieldsets = getFieldsets(root);
    if (fieldsets.length < 2) return;

    const changedIndex = fieldsets.findIndex((fieldset) => fieldset.contains(target));
    if (changedIndex === -1) return;

    const selected = fieldsets.map(checkedValue);

    // If the current selection already maps to a real variant, do nothing.
    const exact = variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
    if (exact) return;

    // Find available variants that honor the option the shopper just changed.
    const candidates = variants.filter(
      (variant) => variant.available && variant.options[changedIndex] === target.value
    );
    if (!candidates.length) return; // nothing in stock for this choice — let the theme show "unavailable"

    // Prefer the candidate that preserves the most other selections, weighting
    // earlier options (e.g. strength) more heavily than later ones (e.g. pack).
    let best = null;
    let bestScore = -1;
    candidates.forEach((variant) => {
      let score = 0;
      variant.options.forEach((value, i) => {
        if (i === changedIndex) return;
        if (value === selected[i]) score += variant.options.length - i;
      });
      if (score > bestScore) {
        bestScore = score;
        best = variant;
      }
    });
    if (!best) return;

    // Snap the conflicting fieldsets to the chosen variant's values. This does not
    // dispatch further change events; the in-flight event continues bubbling to the
    // theme handler, which reads the corrected state.
    fieldsets.forEach((fieldset, i) => {
      if (i === changedIndex) return;
      const wanted = best.options[i];
      fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === wanted;
      });
    });
  }

  // Delegate from document (capture phase) so a single listener covers every
  // <variant-selects>, including ones re-rendered by the theme after a change.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!target || target.type !== 'radio') return;
      const selects = target.closest && target.closest('variant-selects');
      if (selects) onChange(selects, event);
    },
    true
  );
})();
